import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { FiExternalLink, FiPlus, FiRefreshCw, FiLink, FiBookmark, FiCheckCircle, FiFolder, FiX, FiChevronLeft, FiChevronRight, FiGrid, FiList } from "react-icons/fi";
import { usePageHeader } from "../hooks/usePageHeader";
import { SkeletonList } from "../Components/Skeleton";
import { client } from "../Clients";
import type { BCCADMINBaseModelsPageTreeNode, BCCADMINBaseModelsPageListItem } from "../API/Models";
import PageTreePanel from "../Components/PageTree/PageTreePanel";
import TaxonomyMultiSelect from "../Components/TaxonomyMultiSelect";
import { useSelector } from "react-redux";
import type { IApplicationState } from "../Redux/Reducers";

const DEFAULT_LANGUAGE = "fr";
const DEFAULT_DOMAIN = "https://www.bcc.cd";
const PAGE_SIZE_OPTIONS = [30, 40, 50];

const CATEGORY_FILTERS = [
	{ key: "all", label: "All", pathPrefix: undefined as string | undefined },
	{ key: "communiques", label: "Communiqués", pathPrefix: "communique/" },
	{ key: "statistiques", label: "Statistiques", pathPrefix: "statistiques/" },
	{ key: "publications", label: "Publications", pathPrefix: "publications/" },
	{ key: "evenements", label: "Événements", pathPrefix: "evenements/" }
];

/**
 * "Pages" browser: a server-paged list of every editable site page next to the folder tree
 * (PageTreePanel). One endpoint feeds the list : GET /api/content/page/list with page/size,
 * free-text search, a slug path prefix and a taxonomy-id filter : and every card/row click routes
 * into the editor by id (/page/{id}) so PageEditor can take its fast by-id load path. Two prefix
 * filter sources exist on purpose: the tree's folder selection wins over the category chips
 * (a folder click is the more specific intent), and picking one clears the other.
 */
const SitePages: React.FC = () => {
	const history = useHistory();
	const [items, setItems] = useState<BCCADMINBaseModelsPageListItem[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
	const [loadingList, setLoadingList] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
	const [pathFilter, setPathFilter] = useState<string>("all");
	const [activeFolder, setActiveFolder] = useState<string | null>(null);
	const [taxonomyFilterIds, setTaxonomyFilterIds] = useState<string[]>([]);
	const taxonomies = useSelector((state: IApplicationState) => state.taxonomies.items);

	usePageHeader({ title: "Pages" });

	const pathPrefix = activeFolder != null && activeFolder !== ""
		? activeFolder.replace(/\/+$/, "").replace(/^\/+/, "")
		: (CATEGORY_FILTERS.find((f) => f.key === pathFilter)?.pathPrefix ?? undefined);

	const loadList = useCallback(
		async (pageNum: number, size: number) => {
			setLoadingList(true);
			setError(null);
			try {
				const taxonomyFilter = taxonomyFilterIds.length ? taxonomyFilterIds.join(",") : undefined;
				const res = await client.content.apiContentPageListGet(
					DEFAULT_LANGUAGE,
					pageNum,
					size,
					search.trim() || undefined,
					pathPrefix || undefined,
					taxonomyFilter
				);
				const data = res.data;
				setItems(data?.items ?? []);
				setTotalCount(data?.totalCount ?? 0);
				setPage(data?.page ?? 1);
			} catch (err) {
				console.error(err);
				setError("Failed to load pages list.");
				setItems([]);
				setTotalCount(0);
			} finally {
				setLoadingList(false);
			}
		},
		[search, pathPrefix, taxonomyFilterIds]
	);

	// loadList's identity changes with search/pathPrefix/taxonomy state and the effect keys on it,
	// so any filter edit (including each search keystroke) reloads the list. The Search button and
	// Enter key therefore exist to reset to page 1, not to trigger the fetch itself.
	useEffect(() => {
		loadList(page, pageSize);
	}, [pathPrefix, page, pageSize, loadList]);

	const onFilterOrSearchChange = () => {
		setPage(1);
		loadList(1, pageSize);
	};
	const onTaxonomyFilterChange = (next: string[]) => {
		setTaxonomyFilterIds(next);
		setPage(1);
	};

	// Heuristic badge counters: recursively count non-empty "href" (outgoing links) and "id"
	// (content references) keys anywhere in the raw list item. Indicative only : the server doesn't
	// expose real link/reference counts on the list endpoint, and walking the payload is close
	// enough for a browse-time signal.
	const countByKey = (value: any, keyName: string): number => {
		if (value == null) return 0;
		if (Array.isArray(value)) return value.reduce((acc, v) => acc + countByKey(v, keyName), 0);
		if (typeof value === "object") {
			let sum = 0;
			for (const [k, v] of Object.entries(value)) {
				if (k === keyName && typeof v === "string" && (v as string).trim().length > 0) sum += 1;
				sum += countByKey(v, keyName);
			}
			return sum;
		}
		return 0;
	};

	const countLinks = (p: BCCADMINBaseModelsPageListItem) => countByKey(p, "href");
	const countReferences = (p: BCCADMINBaseModelsPageListItem) => countByKey(p, "id");

	const goToCreate = (prefix: string) => {
		history.push(`/new-page?lang=${encodeURIComponent(DEFAULT_LANGUAGE)}&prefix=${encodeURIComponent(prefix)}`);
	};

	const goToPage = (p: BCCADMINBaseModelsPageListItem) => {
		const langCode = p.language || DEFAULT_LANGUAGE;
		if (p.id) {
			history.push(`/page/${encodeURIComponent(p.id)}`);
		} else {
			history.push(`/page/${encodeURIComponent(langCode)}/${encodeURIComponent(p.slug || "")}`);
		}
	};

	const handleTreeSelect = (node: BCCADMINBaseModelsPageTreeNode) => {
		const nodeId = node?.id ?? null;
		const nodeSlug = node?.slug ?? node?.path ?? null;
		const nodeLanguage = (node?.language ?? DEFAULT_LANGUAGE)?.toString();
		if (nodeId) {
			history.push(`/page/${encodeURIComponent(nodeId)}`);
			return;
		}
		if (nodeSlug) {
			history.push(`/page/${encodeURIComponent(nodeLanguage)}/${encodeURIComponent(nodeSlug)}`);
		}
	};

	const activeFolderPrefix = activeFolder ? `${activeFolder.replace(/\/+$/, "")}/` : "";
	const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
	const canPrev = page > 1;
	const canNext = page < totalPages;

	return (
		<div className="flex h-full relative">
			{loadingList && (
				<div className="absolute inset-0 z-40 overflow-hidden bg-background p-6">
					<SkeletonList rows={9} />
				</div>
			)}
			<div className="flex w-full gap-6 p-6">
				<PageTreePanel
					className="w-full max-w-[350px] shrink-0 sticky top-4 self-start"
					language={DEFAULT_LANGUAGE}
					activeFolderPath={activeFolder}
					onSelectFolder={(path) => {
						setActiveFolder(path);
						setPage(1);
					}}
					onSelectPage={handleTreeSelect}
				/>
				<div className="flex-1 min-w-0 space-y-4">
					<div className="flex items-center justify-between gap-3">
						<div>
							<p className="text-xs uppercase tracking-[0.3em] text-slate-400">Pages</p>
							<h1 className="text-xl font-semibold text-slate-900">Browse and create</h1>
							<p className="text-sm text-slate-600">
								Use the cards below to start a new page. Click any card to edit, and use the public URL (e.g.{" "}
								<code className="font-mono">/page/&lt;id&gt;</code>) when linking from the site.
							</p>
						</div>
						<div className="flex flex-wrap items-center gap-2">
							<button
								className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
								onClick={() => goToCreate("")}
							>
								<FiPlus /> Create page
							</button>
							<button
								className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
								onClick={() => window.open(`${DEFAULT_DOMAIN}/`, "_blank")}
							>
								<FiExternalLink /> Open site
							</button>
						</div>
					</div>

					{error && (
						<div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
							{error}
						</div>
					)}

					{activeFolder && (
						<div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
							<div className="flex items-center gap-3 text-slate-700">
								<div className="rounded-full border border-slate-200 bg-slate-50 p-2">
									<FiFolder className="h-4 w-4" />
								</div>
								<div>
									<p className="text-xs uppercase tracking-[0.3em] text-slate-400">Folder view</p>
									<p className="text-sm font-semibold text-slate-900">/{activeFolder}</p>
								</div>
							</div>
							<div className="flex flex-wrap items-center gap-2">
								<button
									type="button"
									onClick={() => goToCreate(activeFolderPrefix)}
									className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
								>
									<FiPlus /> Create in folder
								</button>
								<button
									type="button"
									onClick={() => {
										setActiveFolder(null);
										setPage(1);
									}}
									className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
								>
									<FiX /> Clear
								</button>
							</div>
						</div>
					)}

					<div className="flex flex-wrap items-end gap-3">
						<div className="min-w-[260px] max-w-[360px]">
							<TaxonomyMultiSelect
								label="Taxonomies"
								options={taxonomies}
								value={taxonomyFilterIds}
								onChange={onTaxonomyFilterChange}
								placeholder="Filter taxonomies"
							/>
						</div>
						<input
							className="w-full max-w-xl rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
							placeholder="Search pages by slug, title, or description"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							onKeyDown={(e) => e.key === "Enter" && onFilterOrSearchChange()}
						/>
						<button
							className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
							onClick={() => onFilterOrSearchChange()}
							title="Search"
						>
							Search
						</button>
						<button
							className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
						onClick={() => loadList(page, pageSize)}
							title="Refresh list"
						>
							<FiRefreshCw />
						</button>
					<div className="flex items-center gap-2">
						<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Per page</label>
						<select
							value={pageSize}
							onChange={(event) => {
								const nextSize = Number(event.target.value);
								setPageSize(nextSize);
								setPage(1);
								loadList(1, nextSize);
							}}
							className="rounded-lg border border-slate-200 px-2 py-2 text-sm focus:border-slate-400 focus:outline-none"
						>
							{PAGE_SIZE_OPTIONS.map((option) => (
								<option key={option} value={option}>
									{option}
								</option>
							))}
						</select>
					</div>
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={() => setViewMode("grid")}
							className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold transition ${
								viewMode === "grid"
									? "bg-slate-900 text-white shadow-sm"
									: "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
							}`}
						>
							<FiGrid /> Grid
						</button>
						<button
							type="button"
							onClick={() => setViewMode("list")}
							className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold transition ${
								viewMode === "list"
									? "bg-slate-900 text-white shadow-sm"
									: "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
							}`}
						>
							<FiList /> List
						</button>
					</div>
					</div>

					<div className="flex flex-wrap items-center gap-2">
						{CATEGORY_FILTERS.map((filter) => {
							const isActive = filter.key === pathFilter && !activeFolder;
							return (
								<button
									key={`cat-${filter.key}`}
									className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
										isActive
											? "border-slate-800 bg-slate-800 text-white shadow-sm"
											: "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
									}`}
									onClick={() => {
										setPathFilter(filter.key);
										setActiveFolder(null);
										setPage(1);
									}}
								>
									{filter.label}
								</button>
							);
						})}
					</div>

					{viewMode === "grid" ? (
						<div className="grid grid-cols-2 md:grid-cols-3 gap-4">
							{items.length === 0 ? (
								<div className="col-span-full rounded-2xl border border-slate-200 bg-white p-4 shadow-sm text-sm text-slate-600">
									No pages found.
								</div>
							) : (
								items.map((p) => {
									const decodedSlug =
										p.slug != null
											? (() => {
													try {
														return decodeURIComponent(p.slug);
													} catch {
														return p.slug;
													}
											  })()
											: "";
									const meta = (p.metadata ?? {}) as { description?: string };
									const publicPath = p.id ? `/page/${p.id}` : `/${decodedSlug || ""}`;
									const linksCount = countLinks(p);
									const refsCount = countReferences(p);
									return (
										<div
											key={`${p.slug ?? ""}-${p.id ?? ""}`}
											className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm cursor-pointer hover:border-slate-300 transition"
											onClick={() => goToPage(p)}
										>
											<div className="flex flex-col gap-2 min-w-0">
												<h3 className="text-base font-semibold text-slate-900 truncate" title={p.title ?? decodedSlug}>
													{p.title ?? decodedSlug}
												</h3>
												<p className="text-sm text-slate-600 line-clamp-2 min-h-[2.5rem]">
													{meta.description ?? decodedSlug}
												</p>
												{p.id && (
													<p className="text-[11px] font-mono text-slate-500 truncate" title={p.id}>
														id: {p.id}
													</p>
												)}
												<p className="text-xs font-mono text-slate-500 truncate" title={decodedSlug || publicPath}>
													{decodedSlug || publicPath}
												</p>
											</div>
											<div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
												<span className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5">
													<FiLink className="text-slate-400 shrink-0" /> {linksCount}
												</span>
												<span className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5">
													<FiBookmark className="text-slate-400 shrink-0" /> {refsCount}
												</span>
												<span className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5">
													<FiCheckCircle className="text-emerald-500 shrink-0" /> Published
												</span>
											</div>
										</div>
									);
								})
							)}
						</div>
					) : (
						<div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
							<table className="min-w-full divide-y divide-slate-200 text-sm">
								<thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
									<tr>
										<th scope="col" className="px-4 py-3 text-left">Title</th>
										<th scope="col" className="px-4 py-3 text-left">Slug</th>
										<th scope="col" className="px-4 py-3 text-left">Links</th>
										<th scope="col" className="px-4 py-3 text-left">Refs</th>
										<th scope="col" className="px-4 py-3 text-left">Status</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-slate-100">
									{items.length === 0 ? (
										<tr>
											<td colSpan={5} className="px-4 py-6 text-center text-slate-500">
												No pages found.
											</td>
										</tr>
									) : (
										items.map((p) => {
											const decodedSlug =
												p.slug != null
													? (() => {
															try {
																return decodeURIComponent(p.slug);
															} catch {
																return p.slug;
															}
													  })()
													: "";
											const meta = (p.metadata ?? {}) as { description?: string };
											const title = p.title ?? decodedSlug;
											const linksCount = countLinks(p);
											const refsCount = countReferences(p);
											return (
												<tr
													key={`${p.slug ?? ""}-${p.id ?? ""}`}
													className="cursor-pointer hover:bg-slate-50"
													onClick={() => goToPage(p)}
												>
													<td className="px-4 py-3">
														<p className="font-semibold text-slate-900">{title}</p>
														<p className="text-xs text-slate-500 line-clamp-1">{meta.description ?? decodedSlug}</p>
													</td>
													<td className="px-4 py-3 text-xs font-mono text-slate-600 truncate max-w-[260px]">
														{decodedSlug || "-"}
													</td>
													<td className="px-4 py-3">
														<span className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-xs text-slate-600">
															<FiLink className="text-slate-400 shrink-0" /> {linksCount}
														</span>
													</td>
													<td className="px-4 py-3">
														<span className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-xs text-slate-600">
															<FiBookmark className="text-slate-400 shrink-0" /> {refsCount}
														</span>
													</td>
													<td className="px-4 py-3">
														<span className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-xs text-slate-600">
															<FiCheckCircle className="text-emerald-500 shrink-0" /> Published
														</span>
													</td>
												</tr>
											);
										})
									)}
								</tbody>
							</table>
						</div>
					)}

					{totalCount > 0 && (
						<div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3">
							<p className="text-sm text-slate-600">
								Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalCount)} of {totalCount}
							</p>
							<div className="flex items-center gap-2">
								<button
									type="button"
									disabled={!canPrev}
									onClick={() => canPrev && loadList(page - 1, pageSize)}
									className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
								>
									<FiChevronLeft /> Previous
								</button>
								<span className="text-sm text-slate-600">
									Page {page} of {totalPages}
								</span>
								<button
									type="button"
									disabled={!canNext}
									onClick={() => canNext && loadList(page + 1, pageSize)}
									className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
								>
									Next <FiChevronRight />
								</button>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default SitePages;
